/**
 * MY SCHEDULE, IN MY OWN PHONE'S CALENDAR.
 *
 * Jacky, 18 September: *"each crew should also have a subscription calendar that is generated, so they
 * can click and add it to calendar."*
 *
 * The office issues the address (it is a capability URL — see the migration); this is the crew's end of
 * it. Two deliberate choices:
 *
 *   · **The link is shown in full, not hidden behind "send me a link".** A crew pasting it into a
 *     phone in front of the office, or forwarding it to their partner, is the normal case; hiding the
 *     string would mean the office has to read it out.
 *   · **No link issued is not an error and not a button that fails.** It says to ask the office, in
 *     their language, because the crew cannot mint one — the command is staff-only by design.
 */
import {useQuery} from '@tanstack/react-query';
import {Button} from '@aiw/ui';
import {crewCalendarUrl} from '@artemis-group/aiw-contracts';
import {authHeaders, fetchImpl} from '../lib/api';
import {env} from '../lib/env';
import {useT} from '../i18n';
import {useState} from 'react';

type Mine = {
  crew: string; party_id: string; token: string | null; issued_at: string | null;
  rotated_at: string | null; revoked_at: string | null; last_read_at: string | null; reads: number;
};

export function MyCalendar() {
  const {t} = useT();
  const [copied, setCopied] = useState(false);
  const q = useQuery({
    queryKey: ['my-calendar'],
    staleTime: 60_000,
    queryFn: async () => {
      const r = await fetchImpl(`${env.apiBase}/api/my-calendar`, {headers: await authHeaders()});
      if (!r.ok) throw new Error('unavailable');
      return ((await r.json()) as {data: Mine | null}).data;
    },
  });

  if (q.isError) return <section className="rounded-lg border bg-card p-4">
    <h2 className="font-display text-sm font-semibold">{t('cal_title')}</h2>
    <p role="alert" className="mt-1 text-sm text-muted-foreground">{t('cal_failed')}</p>
  </section>;

  const url = q.data?.token ? crewCalendarUrl(env.apiBase, q.data.token, window.location.origin) : null;
  const when = q.data?.last_read_at ? new Date(q.data.last_read_at).toLocaleDateString('en-AU', {day: '2-digit', month: 'short', timeZone: 'Australia/Sydney'}) : null;

  async function copy() {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* the string is on screen; a blocked clipboard is not a failure worth a message */ }
  }

  return <section className="rounded-lg border bg-card p-4">
    <h2 className="font-display text-sm font-semibold">{t('cal_title')}</h2>
    <p className="mt-1 text-sm text-muted-foreground">{t('cal_why')}</p>
    {q.isPending ? null : url ? <>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button onClick={() => void copy()}>{copied ? t('cal_copied') : t('cal_copy')}</Button>
        <a className="text-sm underline" href={`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(url)}`} target="_blank" rel="noreferrer">{t('cal_add')}</a>
      </div>
      <p className="mt-3 text-micro text-muted-foreground">{t('cal_paste')}</p>
      <code className="mt-1 block break-all rounded border bg-muted/40 px-2 py-1 text-micro">{url}</code>
      {when && <p className="mt-2 text-micro text-muted-foreground">{t('cal_read', {date: when})}</p>}
    </> : <p className="mt-3 text-sm">{t('cal_none')}</p>}
  </section>;
}
